import {
  AddIcon,
  Button,
  Col,
  DeleteIcon,
  EditIcon,
  ListTable,
  Modal,
  Row,
  Search,
  Spinner,
} from 'elmo-elements';
import React, { ReactElement, useEffect, useState } from 'react';
import '../settings.scss';
import {
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
} from '@/modules/services';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { getQueryString, getValidPage, pushParamsToUrl } from '@/lib/util';
import { appData } from '@/lib/data';
import './index.scss';
import { TemplateDataType, TemplateSort } from '@/modules/types/template';
import config from '@/lib/config/index';
import SettingsTemplateSelectedRoutes from '@/pages/Templates/templateRoute';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { TemplatePlayerType, defaultTemplateValue } from '@/pages/Templates';
import { Translations } from '@/lib/translation';
import { findIndex } from 'lodash';

const TemplatesComponent = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string>('');

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const currentQueryString = getQueryString(
    location.search,
    appData.pagination.defaultParams,
  );
  const { sort, order } = currentQueryString;
  const pageSizes = appData.pagination.pages;
  const sortableColumns = [
    { key: TemplateSort.NAME, name: 'Template name' },
    { key: 'layout', name: 'Layout' },
    { key: 'playerType', name: 'Player Type' },
    { key: TemplateSort.CREATED_AT, name: 'Created' },
  ];
  const sortedColumnIndex = findIndex(sortableColumns, { key: sort });

  const { data: templateData, isLoading } =
    useGetTemplatesQuery(currentQueryString);
  const [createTemplate, createTemplateStatus] = useCreateTemplateMutation();
  const [deleteTemplate, deleteTemplateStatus] = useDeleteTemplateMutation();

  const handleDeleteTemplate = (templateId: string) => {
    if (!templateId) return;

    deleteTemplate(templateId);
    setDeleteTemplateId('');
    setIsModalOpen(false);
  };

  const getFormatedDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('en-AU', { month: 'short' });

    return `${date.getDate()} ${month} ${date.getFullYear()}`;
  };

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  const handlSearch = (search: string) => {
    pushParamsToUrl(
      {
        search: searchKeyword,
        page: 1,
      },
      history,
    );
  };

  const handleSearchChange = (searchValue: string) => {
    setSearchKeyword(searchValue);
    if (searchValue === '') pushParamsToUrl({ search: '', page: 1 }, history);
  };

  const renderPlayerType = (type: TemplatePlayerType) => {
    switch (type) {
      case TemplatePlayerType.Default:
        return 'Default';
      case TemplatePlayerType.Modern:
        return 'Modern';

      default:
        return 'N/A';
    }
  };

  const renderActions = (data: TemplateDataType) => [
    {
      icon: <EditIcon />,
      label: 'Edit',
      onClick: () => {
        history.push(
          generatePath(SettingsTemplateSelectedRoutes.path, {
            templateId: data.id,
          }),
        );
      },
    },
    {
      icon: <DeleteIcon />,
      label: 'Remove',
      onClick: () => {
        setDeleteTemplateId(data.id);
        setIsModalOpen(true);
      },
    },
  ];

  useEffect(() => {
    if (!createTemplateStatus.isError && !createTemplateStatus.isSuccess)
      return;

    /* Redirect to edit form after successful creation */
    if (createTemplateStatus.isLoading || !createTemplateStatus.data) return;
    history.push(
      generatePath(SettingsTemplateSelectedRoutes.path, {
        templateId: createTemplateStatus.data.id,
      }),
    );

    let message = Translations.settings.Templates.createTemplate.Fail;
    if (createTemplateStatus.isSuccess) {
      message = Translations.settings.Templates.createTemplate.Success;
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [createTemplateStatus]);

  useEffect(() => {
    if (!deleteTemplateStatus.isError && !deleteTemplateStatus.isSuccess)
      return;

    let message = Translations.settings.Templates.deleteTemplate.Fail;
    if (deleteTemplateStatus.isSuccess) {
      message = Translations.settings.Templates.deleteTemplate.Success;
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [deleteTemplateStatus]);

  if (isLoading) return <Spinner />;

  return (
    <>
      <Modal
        id="ModalDialog"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => {
          setDeleteTemplateId('');
          setIsModalOpen(false);
        }}
        primaryButton={
          <Button
            type="danger"
            isBlock={true}
            onClick={() => handleDeleteTemplate(deleteTemplateId)}
          >
            Continue
          </Button>
        }
        title="Delete Template"
      >
        Access to this Template will be lost once it is delete. This action
        cannot be undone.
      </Modal>
      <Row className="template-header">
        <Col span={12}>
          <Search
            value={searchKeyword}
            isVisible
            onChange={(value) => {
              handleSearchChange(value);
            }}
            onSubmit={() => {
              handlSearch(searchKeyword);
            }}
            isSubmitEnabledAlways={true}
          />
        </Col>
        <Col span={3}>
          <Button
            icon={<AddIcon />}
            onClick={() => {
              createTemplate({ ...defaultTemplateValue });
            }}
            id="add-new-button"
          >
            Add new
          </Button>
        </Col>
      </Row>
      <ListTable
        ariaLabel="List of Templates"
        sortColumn={sortedColumnIndex}
        sortDirection={order.toLowerCase()}
        onSort={handleSort}
      >
        <ListTable.Header>
          <ListTable.Column label="Name" sortable={true} />
          <ListTable.Column label="Layout" />
          <ListTable.Column label="Player Type" />
          <ListTable.Column label="Date" sortable={true} />
        </ListTable.Header>
        <ListTable.Body>
          {!templateData ? (
            <div>No Template available</div>
          ) : (
            templateData?.data.map((template: TemplateDataType, index) => (
              <ListTable.Tr key={template.id} actions={renderActions(template)}>
                <ListTable.Td>{template.name}</ListTable.Td>
                <ListTable.Td>
                  <img
                    src={
                      template.templateMetadata.img
                        ? `${config.slideTemplatePath}${template.templateMetadata.img}`
                        : `${config.assetPath}img-blank.svg`
                    }
                    alt={template.name}
                  />
                </ListTable.Td>
                <ListTable.Td>
                  {renderPlayerType(+template.templateMetadata.playerType)}
                </ListTable.Td>

                <ListTable.Td>
                  <div className="template-dates">
                    <span>
                      {`Created: ${getFormatedDate(`${template.createdAt}`)} `}
                    </span>
                    <span>
                      {`Modified: ${getFormatedDate(
                        `${template.modifiedAt}`,
                      )} `}
                    </span>
                  </div>
                </ListTable.Td>
              </ListTable.Tr>
            ))
          )}
        </ListTable.Body>
        <ListTable.Pagination
          pageSizeOptions={pageSizes}
          pageSize={templateData?.meta.limit ?? pageSizes[0]}
          currentPage={templateData?.meta.page ?? 1}
          onPageChange={(pageNumber) => {
            pushParamsToUrl({ page: pageNumber }, history);
          }}
          onPageSizeChange={(pageSize) => {
            const pageNumber = getValidPage(
              templateData?.meta.limit ?? pageSizes[0],
              pageSize,
              templateData?.meta.page ?? 1,
              templateData?.meta.itemCount ?? 0,
            );

            pushParamsToUrl(
              {
                limit: pageSize,
                page: pageNumber,
              },
              history,
            );
          }}
          totalResults={templateData?.meta.itemCount ?? 0}
        />
      </ListTable>
    </>
  );
};
export default TemplatesComponent;
